import React, { memo, useMemo, forwardRef } from 'react';
import classNames from 'classnames';
import { flexRender } from '@tanstack/react-table';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PropTypes from 'prop-types';
import { BurgerMenuIcon, SortIcon } from 'common/icons';

const HeaderContent = forwardRef(
  (
    {
      id,
      tableHeight,
      label,
      canSort,
      sorted,
      onSort,
      isDragging,
      transform,
      transition,
      getResizeHandler,
      dragAttrs,
      dragListeners,
    },
    ref
  ) => (
    <th
      ref={ref}
      className={classNames('NewBaseTable__col', 'NewBaseTable__head__col', {
        'NewBaseTable__head__col-dragging': isDragging,
        'NewBaseTable__head__col-with-sort': canSort,
      })}
      style={{
        width: `calc(var(--header-${id}-size) * 1px)`,
        transform: CSS.Translate.toString(transform),
        transition,
      }}>
      <div className="NewBaseTable__head__col__inner">
        <button type="button" className="NewBaseTable__draghandle" {...dragAttrs} {...dragListeners}>
          <BurgerMenuIcon />
        </button>
        <span className="NewBaseTable__head__col-label">
          {label}
          {canSort && (
            <button
              type="button"
              className={classNames('NewBaseTable__head__col-arrow', {
                'NewBaseTable__head__col-arrow--up': sorted === 'desc',
                'NewBaseTable__head__col-arrow--down': sorted === 'asc',
              })}
              onClick={onSort}>
              <SortIcon />
            </button>
          )}
        </span>
      </div>

      <button
        type="button"
        className="NewBaseTable__resizer"
        style={{ height: tableHeight }}
        onMouseDown={getResizeHandler()}
        onTouchStart={getResizeHandler()}>
        <div />
      </button>
    </th>
  )
);

const MemoziedHeaderContent = memo(
  HeaderContent,
  (prev, next) =>
    prev.id === next.id &&
    prev.tableHeight === next.tableHeight &&
    prev.isDragging === next.isDragging &&
    prev.transition === next.transition &&
    prev.sorted === next.sorted &&
    CSS.Translate.toString(prev.transform) === CSS.Translate.toString(next.transform)
);

const DraggableHeaderCell = ({ header, tableHeight }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: header.column.id,
  });

  const label = useMemo(
    () => flexRender(header.column.columnDef.header, header.getContext()),
    [header.id, header.column.columnDef.header]
  );

  return (
    <MemoziedHeaderContent
      ref={setNodeRef}
      id={header.id}
      tableHeight={tableHeight}
      label={label}
      isDragging={isDragging}
      transform={transform}
      transition={transition}
      getResizeHandler={header.getResizeHandler}
      dragAttrs={attributes}
      dragListeners={listeners}
      canSort={header.column.getCanSort()}
      sorted={header.column.getIsSorted()}
      onSort={header.column.getToggleSortingHandler()}
    />
  );
};

DraggableHeaderCell.propTypes = {
  header: PropTypes.object.isRequired,
  tableHeight: PropTypes.number.isRequired,
};

export default DraggableHeaderCell;
